/**
 * <s-nav-overlay> — Keeps a desktop menu category "active" so its submenu stays
 * open while the pointer travels from the category to the submenu column.
 *
 * Markup:
 *   [data-nav-item] — Top-level items. The active one receives [data-active].
 *
 * Behavior:
 *   - Hovering an item activates it after a short delay; quickly passing over
 *     other items on the way to the submenu does not switch the active item.
 *   - Keyboard focus activates an item immediately.
 *   - Closing the parent <dialog> clears the active item.
 */
class SNavOverlay extends HTMLElement {
  #ac;
  #timer = 0;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    for (const item of this.querySelectorAll('[data-nav-item]')) {
      item.addEventListener('pointerenter', (event) => {
        if (event.pointerType !== 'mouse') return;
        clearTimeout(this.#timer);
        if (!this.querySelector('[data-nav-item][data-active]')) {
          this.#activate(item);
          return;
        }
        this.#timer = setTimeout(() => this.#activate(item), 150);
      }, opts);
      item.addEventListener('pointerleave', () => clearTimeout(this.#timer), opts);
      item.addEventListener('focusin', () => {
        clearTimeout(this.#timer);
        this.#activate(item);
      }, opts);
    }

    this.closest('dialog')?.addEventListener('close', () => this.#clear(), opts);
  }

  disconnectedCallback() {
    clearTimeout(this.#timer);
    this.#ac?.abort();
  }

  #activate(item) {
    for (const active of this.querySelectorAll('[data-nav-item][data-active]')) {
      if (active !== item) active.removeAttribute('data-active');
    }
    item.setAttribute('data-active', '');
  }

  #clear() {
    clearTimeout(this.#timer);
    for (const active of this.querySelectorAll('[data-nav-item][data-active]')) {
      active.removeAttribute('data-active');
    }
  }
}

customElements.define('s-nav-overlay', SNavOverlay);
